// core
import { Injectable, Inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

// sdk
import { SdkService, SdkApiService } from '@campfire-nx/sdk';

// services
import { RaptorApiService } from './raptor-api.service';
import { RaptorDataService } from '../services/raptor-data.service';
import { RaptorMultichoiceService } from '../services/raptor-multichoice.service';
import { RaptorQuestionService } from '../services/raptor-question.service';
import { RaptorPredicateService } from '../services/raptor-predicate.service';

@Injectable()
export class RaptorActionService extends RaptorApiService {
  managerService!: any;

  constructor(
    http: HttpClient,
    @Inject('environment') environment: any,
    private raptorApiService: RaptorApiService,
    private raptorDataService: RaptorDataService,
    private raptorMultichoiceService: RaptorMultichoiceService,
    private raptorQuestionService: RaptorQuestionService,
    private raptorPredicateService: RaptorPredicateService,
    private sdkService: SdkService,
    public sdkApiService: SdkApiService
  ) {
    super(http, environment);
  }

  register(service: any) {
    this.managerService = service;
  }

  next(formData: any, form: FormGroup): Promise<any> {
    const nextPayLoad = this.constructPayloadForNext(formData, form);

    if (this.managerService && this.managerService.next) {
      return this.managerService.next(nextPayLoad);
    }

    if (formData.actions.next.method === 'PUT') {
      return this.raptorApiService.put(formData.actions.next.url, nextPayLoad).toPromise();
    }
    return this.raptorApiService.post(formData.actions.next.url, nextPayLoad).toPromise();
  }

  back(formData: any): Promise<any> {
    if (this.managerService && this.managerService.back) {
      return Promise.resolve(this.managerService.back(formData));
    }

    return this.raptorApiService.get(formData.actions.back.url).toPromise();
  }

  live(formData: any, form: FormGroup): Promise<any> {
    const nextPayLoad = this.constructPayloadForNext(formData, form);
    return this.patch(formData.actions.live.url, nextPayLoad).toPromise();
  }

  goToEdit(formData: any, questionKey: string): Promise<any> {
    return this.raptorApiService.get(`${formData.actions.review.url}?key=${questionKey}&ref_page=${formData.uuid}`).toPromise();
  }

  jump(url: string): Promise<any> {
    if (this.managerService && this.managerService.jump) {
      return this.managerService.jump();
    }

    return this.raptorApiService.get(url).toPromise();
  }

  sendQuote(actionDetails: any, payload: any): Promise<any> {
    return this.raptorApiService.post(actionDetails.url, payload).toPromise();
  }

  getAutoCompleteChoices(url: string): Observable<any> {
    return this.raptorApiService.get(url).pipe(
      catchError((err) => of({ error: err }))
    );
  }

  validateMember(url: string, payload: any): Promise<any> {
    const payloadData = {
      'value': payload
    };
    return this.raptorApiService.post(url, payloadData).toPromise();
  }

  saveMedicalCondition(url: string, payload: any): Promise<any> {
    return this.raptorApiService.post(url, payload).toPromise();
  }

  getMedicalCondition(url: string): Promise<any> {
    return this.raptorApiService.get(url).toPromise();
  }

  getVehicleChoices(url: string): Observable<any> {
    if (this.sdkService.emmulate === true) {
      return this.sdkApiService.getAutoComplete();
    }

    return this.raptorApiService.get(url).pipe(
      catchError((err) => of({ error: err }))
    );
  }

  setVehicleValues(question: any, formValues: any, payload: any): any {
    const vehicleKeys = ['make', 'model', 'state', 'transmission', 'variant', 'vehicle', 'year'];

    // delete current saved values and set again from scratch
    vehicleKeys.forEach((value) => {
      if (payload[`${question.key}_${value}`]) {
        payload[`${question.key}_${value}`] = null;
      }
    });
    if (formValues[question.key]) {
      for (const key in formValues[question.key]) {
        if (key !== 'vehicle') {
          payload[`${question.key}_${key}`] = formValues[question.key][key];
        }
      }
      payload[question.key] = formValues[question.key].vehicle;
    }

    return payload;
  }

  private constructPayloadForNext(formData: any, form: FormGroup) {
    const formValues = form.getRawValue();
    let payload: any = { ...formData.data }; // make a copy
    const questions: Array<any> = this.raptorQuestionService.getAllQuestions(formData);
    let isSectionVisible: boolean;
    let isQuestionVisible: boolean;

    questions.forEach((question) => {
      if (question.key && question.layout !== 'review' && !question.key.includes('__')) {
        isSectionVisible = true;
        isQuestionVisible = true;
        const section = this.raptorQuestionService.getSectionByQuestionKey(formData, question.key);

        if (section && section.show_if && typeof section.show_if === 'object') {
          isSectionVisible = this.raptorPredicateService.validator(section.show_if, formValues);
        }

        if (question.show_if && typeof question.show_if === 'object') {
          isQuestionVisible = this.raptorPredicateService.validator(question.show_if, formValues);
        }

        if (isSectionVisible && isQuestionVisible) {
          // special case for repeater multiple, the data needs to be mounted onto the question
          if (question.type === 'repeater' && question.layout === 'multiple') {
            this.mountRepeaterData(formValues, question);
          }
          payload[question.key] = this.raptorDataService.createQuestionPayload(formValues[question.key], question);

          if (question.type === 'multiple_choice') {
            this.raptorMultichoiceService.createNonePayload(question, formValues, payload);
          }
          if (question.type === 'vehicle') {
            payload = this.setVehicleValues(question, formValues, payload);
          }
          if (question.type === 'validation') {
            payload = this.setMembershipValues(question, formValues, payload);
          }
          if (question.type === 'mobile') {
            payload = this.setMobileValues(question, formValues, payload);
          }
        } else {
          payload[question.key] = question.type === 'repeater' ? [] : null;
        }
      }
    });

    return payload;
  }

  mountRepeaterData(formValues: any, question: any) {
    let questionData: any = [];

    if (question.fields && question.fields.length) {
      question.fields.forEach((fieldGroup: Array<any>) => {
        const groupData: any = {};
        fieldGroup.forEach((column: any) => {
          column.forEach((field: any) => {
            if (field.key) {
              const fieldValue = this.raptorDataService.createQuestionPayload(formValues[field.key], field);
              const keyArray = field.key.split('_');
              const fieldKey = keyArray.slice(0, -1).join('_');

              groupData[fieldKey] = fieldValue;
            }
          });
        });
        questionData.push(groupData);
        
      });
    }
    question.data = questionData;
  }

  setMembershipValues(question: any, formValues: any, payload: any) {
    payload[question.key] = formValues[question.key];

    if (question.layout === 'bupa_mem_num') {
      payload[question.key] = formValues[question.key].number + '-' + formValues[question.key].suffix;
      payload.bupa_member_type = formValues[question.key].type;
    } else {      
      payload[question.key] = formValues[question.key];
    }

    return payload;
  }

  setMobileValues(question: any, formValues: any, payload: any) {
    if (formValues[question.key].mobileNumber) {
      const mobileNumberSanitised = formValues[question.key].mobileNumber.replace(/\s+/g, '');
      payload[question.key] = mobileNumberSanitised.replace(/^0|^/, `${ formValues[question.key].country } `);
    } else {
      payload[question.key] = "";
    }

    return payload;
  }
}
